/**
 * TanStack Query over the ops route table, one hook per route.
 *
 * Every hook goes through `ApiClient.getParsed`/`postParsed`/`patchParsed`/
 * `del`, the typed edge (`src/lib/api.ts`): the response is parsed against
 * one of `./schemas`'s schemas before a component ever sees it, and a parse
 * failure surfaces as `ApiFailureError({ kind: 'malformed' })` rather than a
 * blank cell. The `ApiClient` itself comes from the session wiring
 * (`src/app/session/client`) — the carved-out import the boundary test in
 * `src/ops/__tests__/boundary.test.ts` allows, because the operator signs in
 * through the same Firebase session as the tenant application.
 *
 * The backend does not exist yet; every hook's method, path and field names
 * are checked against the documented route table. Where that documentation
 * is silent on a shape this plane needs, the shape here is this module's own
 * design and marked ASSUMED below.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApiClient } from '../../app/session/client';
import type { ApiFailureError } from '../../lib/http/errors';
import {
  creditRequestSchema,
  inferenceTaskSchema,
  operationCatalogueRowSchema,
  opsKeySchema,
  opsMovementSchema,
  opsTenantCreatedSchema,
  platformAdminSchema,
  postStatsSettingsSchema,
  privacyThresholdSchema,
  type CreateTenantInput,
  type CreditRequest,
  type InferenceTask,
  type OperationBasis,
  type OperationCatalogueRow,
  type OperationProvider,
  type OperatorSpendRow,
  type OpsKey,
  type OpsMovement,
  type OpsTenantCreated,
  type OpsTenantDetail,
  type OpsTenantRow,
  type PlatformAdmin,
  type PlatformMargin,
  type PostStatsSettings,
  type PrivacyThreshold,
} from './schemas';
import { routePath } from '../../lib/http/routePath';
import { OPS_GET_ROUTES } from './getRoutes';

export const opsKeys = {
  tenants: () => ['ops', 'tenants'] as const,
  tenant: (id: string) => ['ops', 'tenants', id] as const,
  movements: (id: string) => ['ops', 'tenants', id, 'movements'] as const,
  operations: () => ['ops', 'operations'] as const,
  requests: (states: string[]) => ['ops', 'requests', ...states] as const,
  keysList: () => ['ops', 'keys'] as const,
  usage: (from: string, to: string, groupBy: string) =>
    ['ops', 'usage', from, to, groupBy] as const,
  platformMargin: (from: string, to: string) => ['ops', 'usage', 'platform', from, to] as const,
  tenantUsage: (id: string, from: string, to: string) =>
    ['ops', 'tenants', id, 'usage', from, to] as const,
  admins: () => ['ops', 'admins'] as const,
  inference: () => ['ops', 'inference'] as const,
  privacyThresholds: () => ['ops', 'privacy', 'thresholds'] as const,
  postStatsSettings: () => ['ops', 'collection', 'post-stats'] as const,
};

export function useOpsTenants(): UseQueryResult<OpsTenantRow[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<OpsTenantRow[], ApiFailureError>({
    queryKey: opsKeys.tenants(),
    queryFn: () =>
      client.getParsed(routePath(OPS_GET_ROUTES.opsTenants, {}), OPS_GET_ROUTES.opsTenants.schema),
  });
}

export function useOpsTenant(id: string): UseQueryResult<OpsTenantDetail, ApiFailureError> {
  const client = useApiClient();
  return useQuery<OpsTenantDetail, ApiFailureError>({
    queryKey: opsKeys.tenant(id),
    queryFn: () =>
      client.getParsed(
        routePath(OPS_GET_ROUTES.opsTenant, { id }),
        OPS_GET_ROUTES.opsTenant.schema,
      ),
  });
}

/** GET /ops/tenants/{t}/credits/movements. Returned in whatever order the
 *  server sends -- this hook does not sort, so a screen
 *  that wants oldest-first sorts locally rather than the fetch silently
 *  disagreeing with the API. */
export function useOpsMovements(tenantId: string): UseQueryResult<OpsMovement[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<OpsMovement[], ApiFailureError>({
    queryKey: opsKeys.movements(tenantId),
    queryFn: () =>
      client.getParsed(
        routePath(OPS_GET_ROUTES.opsTenantMovements, { tenantId }),
        OPS_GET_ROUTES.opsTenantMovements.schema,
      ),
  });
}

/** GET /ops/operations. The catalogue of what CAN be priced, which is
 *  a different question from what a tenant is about to hit unpriced. */
export function useOperationCatalogue(): UseQueryResult<OperationCatalogueRow[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<OperationCatalogueRow[], ApiFailureError>({
    queryKey: opsKeys.operations(),
    queryFn: () =>
      client.getParsed(
        routePath(OPS_GET_ROUTES.opsOperations, {}),
        OPS_GET_ROUTES.opsOperations.schema,
      ),
  });
}

/** `PUT /ops/operations/{key}`. Every field required: a save is the whole
 *  configuration (`api`'s `OperationIn`). `keyId` is `null` for `platform`,
 *  which calls nobody. */
export interface SaveOperationInput {
  operationKey: string;
  provider: OperationProvider;
  keyId: string | null;
  basis: OperationBasis;
  multiplier: string;
}

export function useSaveOperation(): UseMutationResult<
  OperationCatalogueRow,
  ApiFailureError,
  SaveOperationInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<OperationCatalogueRow, ApiFailureError, SaveOperationInput>({
    mutationFn: (input) =>
      client.putParsed(
        `/ops/operations/${input.operationKey}`,
        {
          provider: input.provider,
          key_id: input.keyId,
          basis: input.basis,
          multiplier: input.multiplier,
        },
        operationCatalogueRowSchema,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: opsKeys.operations() });
    },
  });
}

/** `GET /ops/keys`. Every active or retired key, named by an operation's
 *  own configuration -- the row carries no provider of its own (`api`'s
 *  `KeyOut` docstring). */
export function useOpsKeys(): UseQueryResult<OpsKey[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<OpsKey[], ApiFailureError>({
    queryKey: opsKeys.keysList(),
    queryFn: () =>
      client.getParsed(routePath(OPS_GET_ROUTES.opsKeys, {}), OPS_GET_ROUTES.opsKeys.schema),
  });
}

/** `GET /ops/inference`. One row per `TaskType`, always -- every task is
 *  seeded once and never deleted (`api`'s `list_tasks`). */
export function useInferenceTasks(): UseQueryResult<InferenceTask[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<InferenceTask[], ApiFailureError>({
    queryKey: opsKeys.inference(),
    queryFn: () =>
      client.getParsed(
        routePath(OPS_GET_ROUTES.opsInference, {}),
        OPS_GET_ROUTES.opsInference.schema,
      ),
  });
}

/** `PUT /ops/inference/{task}` (`api`'s `InferenceChoiceIn`). A caller that
 *  is only replacing `config` still supplies every other field, read back
 *  off the task's current row: a save is the whole choice, and core refuses
 *  a provider or config it does not accept. */
export interface SaveInferenceConfigInput {
  task: string;
  provider: string;
  config: string;
  providerKeyId: string | null;
  secondaryProvider: string | null;
  secondaryModelId: string | null;
  secondaryProviderKeyId: string | null;
  priority: InferenceTask['priority'];
  flexFallbackAfter: number | null;
}

export function useSaveInferenceConfig(): UseMutationResult<
  InferenceTask,
  ApiFailureError,
  SaveInferenceConfigInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<InferenceTask, ApiFailureError, SaveInferenceConfigInput>({
    mutationFn: (input) =>
      client.putParsed(
        `/ops/inference/${input.task}`,
        {
          provider: input.provider,
          config: input.config,
          provider_key_id: input.providerKeyId,
          secondary_provider: input.secondaryProvider,
          secondary_model_id: input.secondaryModelId,
          secondary_provider_key_id: input.secondaryProviderKeyId,
          priority: input.priority,
          flex_fallback_after: input.flexFallbackAfter,
        },
        inferenceTaskSchema,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: opsKeys.inference() });
    },
  });
}

export function useCreditRequests(
  states: string[],
): UseQueryResult<CreditRequest[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<CreditRequest[], ApiFailureError>({
    queryKey: opsKeys.requests(states),
    queryFn: () => {
      // Repeated `state=X&state=Y`, matching FastAPI's native
      // `Query(list[CreditRequestState] | None)` -- no custom parser on
      // either side.
      const params = new URLSearchParams();
      for (const state of states) params.append('state', state);
      return client.getParsed(
        routePath(OPS_GET_ROUTES.opsCreditRequests, {}, params),
        OPS_GET_ROUTES.opsCreditRequests.schema,
      );
    },
  });
}

export type UsageGroupBy = 'tenant' | 'day_tenant';

export interface SpendQuery {
  from: string;
  to: string;
  groupBy: UsageGroupBy;
}

export function useOperatorSpend(
  q: SpendQuery,
): UseQueryResult<OperatorSpendRow[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<OperatorSpendRow[], ApiFailureError>({
    queryKey: opsKeys.usage(q.from, q.to, q.groupBy),
    queryFn: () =>
      client.getParsed(
        routePath(OPS_GET_ROUTES.opsUsage, {}, { from: q.from, to: q.to, group_by: q.groupBy }),
        OPS_GET_ROUTES.opsUsage.schema,
      ),
  });
}

/** `GET /ops/usage/tenants/{t}`. Already scoped to one tenant server-side, so
 *  unlike `useOperatorSpend` this sends no `groupBy` -- the route now honours
 *  the param on both endpoints, but its default, `'day_tenant'`, is the grain
 *  this caller wants, so there is nothing to pick. */
export function useTenantSpend(
  tenantId: string,
  q: { from: string; to: string },
): UseQueryResult<OperatorSpendRow[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<OperatorSpendRow[], ApiFailureError>({
    queryKey: opsKeys.tenantUsage(tenantId, q.from, q.to),
    queryFn: () =>
      client.getParsed(
        routePath(OPS_GET_ROUTES.opsTenantUsage, { tenantId }, { from: q.from, to: q.to }),
        OPS_GET_ROUTES.opsTenantUsage.schema,
      ),
  });
}

/** `GET /ops/usage/platform`. The platform's own provider cost and margin
 *  for the period, read independently of `useOperatorSpend`: it is the one
 *  figure §18.7 sums server-side rather than derives from the loaded rows. */
export function usePlatformMargin(range: {
  from: string;
  to: string;
}): UseQueryResult<PlatformMargin, ApiFailureError> {
  const client = useApiClient();
  return useQuery<PlatformMargin, ApiFailureError>({
    queryKey: opsKeys.platformMargin(range.from, range.to),
    queryFn: () =>
      client.getParsed(
        routePath(OPS_GET_ROUTES.opsPlatformUsage, {}, { from: range.from, to: range.to }),
        OPS_GET_ROUTES.opsPlatformUsage.schema,
      ),
  });
}

/** `POST /ops/tenants/{t}/credits/grant  {credits, note}`. The response is
 *  the movement the grant created; the balance itself is re-read from
 *  `useOpsTenant`, not derived from this response -- no balance-after
 *  preview. */
export interface GrantCreditsInput {
  tenantId: string;
  credits: number;
  note: string | null;
}

export function useGrantCredits(): UseMutationResult<
  OpsMovement,
  ApiFailureError,
  GrantCreditsInput
> {
  const client = useApiClient();
  return useMutation<OpsMovement, ApiFailureError, GrantCreditsInput>({
    mutationFn: ({ tenantId, credits, note }) =>
      client.postParsed(
        `/ops/tenants/${tenantId}/credits/grant`,
        { credits, note },
        opsMovementSchema,
      ),
  });
}

/** `POST /ops/tenants/{t}/credits/adjust  {credits, note}` -- `note` is
 *  required: every signed adjustment needs one. `credits` already carries
 *  its sign, so the dialog resolves the operator's `Sign` selection to a
 *  signed integer before calling this hook. */
export interface AdjustCreditsInput {
  tenantId: string;
  credits: number;
  note: string;
}

export function useAdjustCredits(): UseMutationResult<
  OpsMovement,
  ApiFailureError,
  AdjustCreditsInput
> {
  const client = useApiClient();
  return useMutation<OpsMovement, ApiFailureError, AdjustCreditsInput>({
    mutationFn: ({ tenantId, credits, note }) =>
      client.postParsed(
        `/ops/tenants/${tenantId}/credits/adjust`,
        { credits, note },
        opsMovementSchema,
      ),
  });
}

/** `PATCH /ops/credit-requests/{r}  {state, decision_note, granted_credits?,
 *  granted_slots?}`. `decision`/`note` are this hook's own names for the
 *  caller's two inputs; the wire body uses the server's field names. Only
 *  the field matching the request's own `kind` is ever supplied -- core
 *  refuses the other -- and whichever is left out falls back to the
 *  request's own `credits_requested`/`slots_requested`, so a claim
 *  (`{state: 'IN_REVIEW'}`) is unaffected by either. */
export interface DecideRequestInput {
  requestId: string;
  decision: 'GRANTED' | 'DECLINED';
  note: string | null;
  grantedCredits?: number | null;
  grantedSlots?: number | null;
}

export function useDecideRequest(): UseMutationResult<
  CreditRequest,
  ApiFailureError,
  DecideRequestInput
> {
  const client = useApiClient();
  return useMutation<CreditRequest, ApiFailureError, DecideRequestInput>({
    mutationFn: ({ requestId, decision, note, grantedCredits, grantedSlots }) => {
      const body: Record<string, unknown> = { state: decision, decision_note: note };
      if (grantedCredits !== undefined) body.granted_credits = grantedCredits;
      if (grantedSlots !== undefined) body.granted_slots = grantedSlots;
      return client.patchParsed(`/ops/credit-requests/${requestId}`, body, creditRequestSchema);
    },
  });
}

/** `POST /ops/keys  {name, secret}` (api `NewKeyIn`). A key carries no
 *  provider of its own -- its name says which one by convention. `secret` is
 *  the raw key material going TO the server -- a request field, never a
 *  response field, so it does not appear on `opsKeySchema`. */
export interface RegisterKeyInput {
  name: string;
  provider: OperationProvider;
  secret: string;
}

export function useRegisterKey(): UseMutationResult<OpsKey, ApiFailureError, RegisterKeyInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<OpsKey, ApiFailureError, RegisterKeyInput>({
    gcTime: 0,
    mutationFn: (input) => client.postParsed('/ops/keys', input, opsKeySchema),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: opsKeys.keysList() });
    },
  });
}

/** `POST /ops/keys/{k}/renew  {secret, grace_seconds}` (api `RenewKeyIn`).
 *  The key's own name is unchanged; `grace_seconds` is the superseded
 *  secret's, required with no default (api `MAX_PARAMETER_GRACE_SECONDS`):
 *  pulling a leaked key and a routine rotation want different windows. */
export interface RenewKeyInput {
  keyId: string;
  secret: string;
  graceSeconds: number;
}

export function useRenewKey(): UseMutationResult<OpsKey, ApiFailureError, RenewKeyInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<OpsKey, ApiFailureError, RenewKeyInput>({
    gcTime: 0,
    mutationFn: ({ keyId, secret, graceSeconds }) =>
      client.postParsed(
        `/ops/keys/${keyId}/renew`,
        { secret, grace_seconds: graceSeconds },
        opsKeySchema,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: opsKeys.keysList() });
    },
  });
}

/** `POST /ops/keys/{k}/retire`, no body: the api refuses a key still named
 *  by `used_by` (409) rather than taking a grace window from the caller. */
export interface RetireKeyInput {
  keyId: string;
}

export function useRetireKey(): UseMutationResult<OpsKey, ApiFailureError, RetireKeyInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<OpsKey, ApiFailureError, RetireKeyInput>({
    mutationFn: ({ keyId }) => client.postParsed(`/ops/keys/${keyId}/retire`, {}, opsKeySchema),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: opsKeys.keysList() });
    },
  });
}
// There is no mutation here that deletes anything. DELETE /ops/tenants/{t} is
// not called by this plane, whether or not the API still exposes it (§18.3).

export function useCreateTenant(): UseMutationResult<
  OpsTenantCreated,
  ApiFailureError,
  CreateTenantInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<OpsTenantCreated, ApiFailureError, CreateTenantInput>({
    mutationFn: (input) => client.postParsed('/ops/tenants', input, opsTenantCreatedSchema),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: opsKeys.tenants() });
    },
  });
}

export function useOpsAdmins(): UseQueryResult<PlatformAdmin[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<PlatformAdmin[], ApiFailureError>({
    queryKey: opsKeys.admins(),
    queryFn: () =>
      client.getParsed(routePath(OPS_GET_ROUTES.opsAdmins, {}), OPS_GET_ROUTES.opsAdmins.schema),
  });
}

/** `POST /ops/admins  {email, note?}`. Creating a `users` row is Firebase's
 *  job (architecture §3), not this route's, so a 404 here means no such row
 *  exists yet -- the screen reads that as "hasn't signed in", not "not
 *  found". `note` is recorded alongside who granted access (copy deck
 *  `admins.grant.note.hint`); an empty note is the same as no note, so it is
 *  left off the wire rather than sent as `""`. */
export interface AddAdminInput {
  email: string;
  note?: string;
}

export function useAddAdmin(): UseMutationResult<PlatformAdmin, ApiFailureError, AddAdminInput> {
  const client = useApiClient();
  return useMutation<PlatformAdmin, ApiFailureError, AddAdminInput>({
    mutationFn: ({ email, note }) =>
      client.postParsed('/ops/admins', note ? { email, note } : { email }, platformAdminSchema),
  });
}

/** `DELETE /ops/admins/{user_id}`. `revoke_platform_admin` refuses to
 *  remove the last administrator server-side; a DELETE carries no response
 *  body to parse, so the caller re-reads the roster (`useOpsAdmins`) rather
 *  than trusting a returned row. */
export interface RevokeAdminInput {
  userId: string;
}

export function useRevokeAdmin(): UseMutationResult<void, ApiFailureError, RevokeAdminInput> {
  const client = useApiClient();
  return useMutation<void, ApiFailureError, RevokeAdminInput>({
    mutationFn: ({ userId }) => client.del(`/ops/admins/${userId}`),
  });
}

/** `GET /ops/privacy/thresholds`. Both k thresholds, value and switch. */
export function usePrivacyThresholds(): UseQueryResult<PrivacyThreshold[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<PrivacyThreshold[], ApiFailureError>({
    queryKey: opsKeys.privacyThresholds(),
    queryFn: () =>
      client.getParsed(
        routePath(OPS_GET_ROUTES.opsPrivacyThresholds, {}),
        OPS_GET_ROUTES.opsPrivacyThresholds.schema,
      ),
  });
}

/** `PUT /ops/privacy/thresholds/{key}  {value, enabled}`. */
export interface SavePrivacyThresholdInput {
  key: PrivacyThreshold['key'];
  value: number;
  enabled: boolean;
}

export function useSavePrivacyThreshold(): UseMutationResult<
  PrivacyThreshold,
  ApiFailureError,
  SavePrivacyThresholdInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<PrivacyThreshold, ApiFailureError, SavePrivacyThresholdInput>({
    mutationFn: ({ key, value, enabled }) =>
      client.putParsed(
        `/ops/privacy/thresholds/${key}`,
        { value, enabled },
        privacyThresholdSchema,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: opsKeys.privacyThresholds() });
    },
  });
}

/** `GET /ops/collection/post-stats`. */
export function usePostStatsSettings(): UseQueryResult<PostStatsSettings, ApiFailureError> {
  const client = useApiClient();
  return useQuery<PostStatsSettings, ApiFailureError>({
    queryKey: opsKeys.postStatsSettings(),
    queryFn: () =>
      client.getParsed(
        routePath(OPS_GET_ROUTES.opsPostStats, {}),
        OPS_GET_ROUTES.opsPostStats.schema,
      ),
  });
}

/** `PUT /ops/collection/post-stats`, the whole row. */
export function useSavePostStatsSettings(): UseMutationResult<
  PostStatsSettings,
  ApiFailureError,
  PostStatsSettings
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<PostStatsSettings, ApiFailureError, PostStatsSettings>({
    mutationFn: (body) =>
      client.putParsed('/ops/collection/post-stats', body, postStatsSettingsSchema),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: opsKeys.postStatsSettings() });
    },
  });
}
